import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { isSupportedImageFile } from '@utils/imageFile';
import { lockBodyScroll, unlockBodyScroll } from '@utils/bodyScrollLock';
import { coverCropImage, setPostImage } from '@images/imageUrls';
import styles from './index.module.scss';

interface ImageCropperModalProps {
  visible: boolean;
  /** 无正文图片时，由发帖页的文件选择器提供初始图片。 */
  imageSrc?: string;
  bodyImages?: string[];
  title?: string;
  summary?: string;
  author?: string;
  avatar?: string;
  onConfirm: (file: File) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const ratios = [
  { label: '2:3', value: 2 / 3, icon: coverCropImage.portrait, width: 17, height: 25.5 },
  { label: '1:1', value: 1, icon: coverCropImage.square, width: 20, height: 20 },
  { label: '2:1', value: 2, icon: coverCropImage.landscape, width: 30, height: 15 },
];

function CoverSession({ imageSrc = '', bodyImages = [], title, summary, author, avatar, onConfirm, onCancel, loading }: ImageCropperModalProps) {
  const [step, setStep] = useState<'select' | 'crop'>(imageSrc ? 'crop' : 'select');
  const [uploadedImage, setUploadedImage] = useState(imageSrc);
  const [selected, setSelected] = useState(imageSrc);
  const [ratio, setRatio] = useState(2 / 3);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discard, setDiscard] = useState(false);
  const [error, setError] = useState('');
  const cropper = useRef<ReactCropperElement>(null);
  const previews = [useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null)];
  const input = useRef<HTMLInputElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const discardDialog = useRef<HTMLDivElement>(null);
  const localUrls = useRef<string[]>([]);
  const mounted = useRef(true);
  const pending = useRef(false);
  const frame = useRef(0);
  const busy = saving || loading;
  const images = Array.from(new Set(bodyImages));
  const requestClose = () => {
    if (pending.current || loading) return;
    if (uploadedImage) setDiscard(true);
    else onCancel();
  };

  useEffect(() => {
    mounted.current = true;
    const previousFocus = document.activeElement as HTMLElement;
    lockBodyScroll();
    dialog.current?.focus();
    return () => {
      mounted.current = false;
      unlockBodyScroll();
      cancelAnimationFrame(frame.current);
      localUrls.current.forEach(url => URL.revokeObjectURL(url));
      previousFocus?.focus();
    };
  }, []);

  useEffect(() => {
    (discard ? discardDialog : dialog).current?.focus();
  }, [discard, step]);

  useEffect(() => {
    if (content.current) content.current.scrollTop = 0;
  }, [step]);

  useEffect(() => {
    if (step !== 'crop' || ready) return;
    const timer = window.setTimeout(() => setError('图片加载失败，请返回选择其他图片或重新上传'), 15000);
    return () => window.clearTimeout(timer);
  }, [step, selected, ready]);

  const updatePreview = () => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const instance = cropper.current?.cropper;
      if (!instance) return;
      try {
        const canvas = instance.getCroppedCanvas({ width: 560, maxWidth: 560, maxHeight: 840 });
        if (!canvas) return;
        previews.forEach(ref => {
          const target = ref.current;
          if (!target) return;
          target.width = canvas.width;
          target.height = canvas.height;
          target.getContext('2d')?.drawImage(canvas, 0, 0);
        });
      } catch {
        setError('无法读取这张图片，请返回后从本地上传');
      }
    });
  };

  const confirm = async () => {
    if (!ready || pending.current || loading) return;
    pending.current = true;
    setSaving(true);
    setError('');
    try {
      const canvas = cropper.current?.cropper.getCroppedCanvas({ maxWidth: 4096, maxHeight: 4096, fillColor: '#fff', imageSmoothingEnabled: true, imageSmoothingQuality: 'high' });
      if (!canvas) throw new Error('图片尚未加载完成');
      const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('图片裁剪失败')), 'image/jpeg', 0.9));
      await onConfirm(new File([blob], 'cover.jpg', { type: 'image/jpeg' }));
    } catch {
      if (mounted.current) setError('封面设置失败，请重试；若图片无法裁剪，请返回后从本地上传');
    } finally {
      pending.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const upload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!isSupportedImageFile(file)) { setError('请选择图片文件'); return; }
    if (file.size > 50 * 1024 * 1024) { setError('图片大小不能超过 50MB'); return; }
    const url = URL.createObjectURL(file);
    localUrls.current.push(url);
    setUploadedImage(url);
    setSelected(url);
    setReady(false);
    setError('');
  };

  const removeUploadedImage = () => {
    if (selected === uploadedImage) setSelected('');
    setUploadedImage('');
    localUrls.current.forEach(url => URL.revokeObjectURL(url));
    localUrls.current = [];
    setReady(false);
    setError('');
  };

  const previousStep = () => {
    // 返回选图时保留独立上传的封面，直到用户主动删除或关闭弹窗。
    cancelAnimationFrame(frame.current);
    setStep('select');
    setReady(false);
    setError('');
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation();
      if (discard) setDiscard(false); else requestClose();
    }
    if (event.key === 'Tab') {
      const root = discard ? discardDialog.current : dialog.current;
      const items = Array.from(root?.querySelectorAll<HTMLElement>('button:not(:disabled), [tabindex="0"]') || [])
        .filter(item => !item.closest('[hidden]'));
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === root)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === root)) { event.preventDefault(); first?.focus(); }
    }
  };

  const previewCard = (double: boolean) => <article className={double ? styles.doubleCard : styles.singleCard}>
    {!double && <div className={styles.author}>{avatar ? <img src={avatar} alt="" /> : <i />}
      <span>{author || '作者'}<small>刚刚发布</small></span></div>}
    <canvas ref={previews[double ? 1 : 0]} aria-label={double ? '双列封面' : '单列封面'}
      style={{ width: double ? '100%' : ratio === 2 / 3 ? 115 : ratio === 1 ? 115 : '100%', aspectRatio: String(ratio) }} />
    <strong>{title || '帖子标题'}</strong>
    {!double && <p>{summary || '正文内容将在这里展示'}</p>}
    <div className={styles.meta}>
      <span className={styles.metaLabel}>{double ? author || '作者' : '发布于刚刚'}</span>
      <div className={styles.previewActions}>
        {!double && <span className={styles.previewAction}><img src={coverCropImage.comment} alt="" />首评</span>}
        <span className={styles.previewAction}><img className={double ? styles.doubleLikeIcon : styles.likeIcon} src={double ? coverCropImage.doubleLike : coverCropImage.like} alt="" />首赞</span>
      </div>
    </div>
  </article>;

  return createPortal(<div className={styles.modalOverlay} onKeyDown={onKeyDown}>
    <div ref={dialog} role="dialog" aria-modal={!discard} aria-label="设置封面" aria-hidden={discard || undefined} tabIndex={-1}
      className={`${styles.customModal} ${step === 'crop' ? styles.cropModal : ''}`}>
      <header><h3>设置封面</h3><button type="button" aria-label="关闭" disabled={busy} onClick={requestClose}>×</button></header>
      <h4>{step === 'select' ? '选择图片' : '选择裁剪比例'}</h4>
      <div ref={content} className={styles.content} data-body-scroll-lock-scrollable>
        {step === 'select' && <section>
          <div className={styles.imageGrid}>
            {uploadedImage ? <div className={styles.uploadedThumbnail}>
              <button type="button" aria-label="选择上传的封面图片" aria-pressed={selected === uploadedImage}
                className={`${styles.thumbnail} ${selected === uploadedImage ? styles.selected : ''}`}
                onClick={() => { if (selected !== uploadedImage) setReady(false); setSelected(uploadedImage); setError(''); }}>
                <img src={uploadedImage} alt="上传的封面图片" />
              </button>
              <button type="button" className={styles.removeUpload} aria-label="删除上传的封面图片" onClick={removeUploadedImage}>
                <img src={setPostImage.deleteCover} alt="" />
              </button>
            </div> : <button type="button" className={styles.upload} onClick={() => input.current?.click()}><img src={setPostImage.addCover} alt="" />上传图片</button>}
            {images.map((src, index) => <button type="button" key={src} aria-label={`选择图片 ${index + 1}`} aria-pressed={selected === src}
              className={`${styles.thumbnail} ${selected === src ? styles.selected : ''}`} onClick={() => { if (selected !== src) setReady(false); setSelected(src); setError(''); }}>
              <img src={src} alt={`图片 ${index + 1}`} />
            </button>)}
          </div>
        </section>}
        {step === 'crop' && <section>
          <div className={styles.ratios}>{ratios.map(item => <button type="button" key={item.label} disabled={busy} aria-pressed={ratio === item.value}
            className={ratio === item.value ? styles.activeRatio : ''} onClick={() => { setRatio(item.value); cropper.current?.cropper.setAspectRatio(item.value); }}>
            <span className={styles.ratioIconSlot}><img className={`${styles.ratioIcon} ${item.value === 2 / 3 ? styles.portraitIcon : ''}`} src={item.icon} alt="" width={item.width} height={item.height} /></span>
            <span className={styles.ratioLabel}>{item.label}</span></button>)}</div>
          <div className={styles.cropLayout}>
            <div className={styles.cropperWrapper}>
              <Cropper key={selected} ref={cropper} src={selected} style={{ height: '100%', width: '100%' }}
                aspectRatio={ratio} viewMode={1} autoCropArea={0.85} dragMode="move" cropBoxMovable={true} cropBoxResizable={false}
                checkCrossOrigin={true} crossOrigin="anonymous" background={true} guides={true}
                ready={() => { setReady(true); setError(''); updatePreview(); }} crop={updatePreview}
                onError={() => { setReady(false); setError('图片加载失败，请返回选择其他图片或重新上传'); }} />
              {!ready && !error && <div className={styles.loading}>图片加载中…</div>}
            </div>
            <aside className={styles.previews} data-body-scroll-lock-scrollable tabIndex={0} aria-label="封面效果预览">
              <h5>单列效果预览</h5>{previewCard(false)}
              <h5>双列效果预览</h5>{previewCard(true)}
            </aside>
          </div>
        </section>}
      </div>
      {error && <p className={styles.error} role="alert">{error}</p>}
      <footer>
        <button type="button" disabled={busy} onClick={step === 'select' ? requestClose : previousStep}>{step === 'select' ? '取消' : '上一步'}</button>
        <button type="button" className={styles.primary} disabled={busy || (step === 'select' ? !selected : !ready)}
          onClick={step === 'select' ? () => { setReady(false); setStep('crop'); setError(''); } : confirm}>{busy ? '处理中…' : step === 'select' ? '下一步' : '确定'}</button>
      </footer>
      <input ref={input} aria-label="上传封面图片" type="file" accept="image/*" hidden onChange={upload} />
    </div>
    {discard && <div className={styles.discardOverlay}><div ref={discardDialog} tabIndex={-1} role="alertdialog" aria-modal="true" aria-label="确认放弃设置封面？" className={styles.discardDialog}>
      <h3>确认放弃设置封面？</h3><footer><button type="button" onClick={() => setDiscard(false)}>继续编辑</button><button type="button" className={styles.primary} onClick={onCancel}>放弃</button></footer>
    </div></div>}
  </div>, document.body);
}

export default function ImageCropperModal(props: ImageCropperModalProps) {
  return props.visible ? <CoverSession {...props} /> : null;
}
